import { ErrorBoundary, init, thirdPartyErrorFilterIntegration } from '@sentry/react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import React, { useEffect } from 'react'
import { createPortal } from 'react-dom'
import { Provider } from 'react-redux'
import { Actions } from '~/actions'
import { CountryProvider, LanguageProvider, useTranslations } from '~/components/Locale'
import { ENV, filesUrl, imageUrl, includes, toGqlProduct } from '~/utils'
import configureStore from '../../configureStore'
import App from './App'

const ignoreErrors = [
	/Load failed/i,
	/Failed to fetch/i,
	/Network Error/i,
	/NetworkError/i,
	/Fetch Error/i,
	/XHR Error/i,
	/AbortError/i,
	/adsbygoogle/i,
	/Cross-origin script load denied/i
]
if (ENV().type !== 'development')
	init({
		dsn: 'https://3ed3af24447f493da4b252dc540e36b9@o1072187.ingest.sentry.io/6070567',
		ignoreTransactions: ['Load failed', 'Failed to fetch', 'cdn/shopifycloud'],
		ignoreErrors,
		denyUrls: [
			/cdn\.shopifycloud/i,
			/shop\.app/i,
			/soundestlink\.com/i,
			/wt\.omnisendlink\.com/i,
			/optimonk\.com/i
		],

		release: import.meta.env.PACKAGE_VERSION,
		environment: ENV().type,
		tracesSampleRate: ENV().sampleRate,

		beforeSend(event, hint) {
			const error = hint.originalException
			if (error instanceof Error && ignoreErrors.some(filter => filter.test(error.message))) {
				return null
			}
			const message = event.message || event.exception?.values?.[0]?.value || ''
			if (ignoreErrors.some(filter => filter.test(message))) {
				return null
			}

			return event
		},

		beforeBreadcrumb(breadcrumb, _hint) {
			if (breadcrumb.category === 'console' && includes(breadcrumb.level, 'info', 'log')) return null
			return breadcrumb
		},
		integrations: [
			thirdPartyErrorFilterIntegration({
				filterKeys: [import.meta.env.PACKAGE_NAME],
				behaviour: 'drop-error-if-contains-third-party-frames'
			})
		]
	})

const store = configureStore()
const queryClient = new QueryClient()

const Fallback: React.FC = () => {
	const t = useTranslations('react')
	return createPortal(
		<div className="portal bg-white text-center text-2xl">
			<div className="m-auto max-w-3xl p-8">
				<h2>Error 500</h2>
				<img src={imageUrl(filesUrl('errordog.png'), '200x')} />
				<p>
					{t?.error_boundary.line_1}
					<br />
					{t?.error_boundary.line_2}
				</p>
				<p>{t?.error_boundary.retry}</p>
				<button className="btn btn--primary" onClick={_ => location.reload()}>
					{t?.actions.try_again}
				</button>
			</div>
		</div>,
		document.body
	)
}

const Root: React.FC = () => {
	useEffect(() => {
		if (window.PRODUCT) {
			const product = toGqlProduct(window.PRODUCT)
			store.dispatch(Actions.loadUpsells(product, product.variants[0]))
		}
		store.dispatch(Actions.loadDeliveryProfiles())
		store.dispatch(Actions.loadMarkets())
		store.dispatch(Actions.loadCart())
		window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], console.warn)
		console.info('Root app initialized.')
	}, [])

	return (
		<ErrorBoundary>
			<QueryClientProvider client={queryClient}>
				<CountryProvider>
					<LanguageProvider>
						<ErrorBoundary fallback={<Fallback />} showDialog>
							<Provider store={store}>
								<React.StrictMode>
									<App />
								</React.StrictMode>
							</Provider>
						</ErrorBoundary>
					</LanguageProvider>
				</CountryProvider>
			</QueryClientProvider>
		</ErrorBoundary>
	)
}

export default Root
